"use strict";
import { Token } from "@uniswap/sdk-core";
import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
const TokenListsContext = createContext(void 0);
export function TokenListsContextProvider({ children }) {
  const listsState = useSelector((state) => state.lists?.byUrl);
  const tokensByChain = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    if (!listsState) {
      return map;
    }
    for (const url of Object.keys(listsState)) {
      const list = listsState[url]?.current;
      if (!list?.tokens) {
        continue;
      }
      for (const info of list.tokens) {
        const chainMap = map.get(info.chainId) ?? /* @__PURE__ */ new Map();
        const key = info.address.toLowerCase();
        if (!chainMap.has(key)) {
          chainMap.set(key, {
            token: new Token(info.chainId, info.address, info.decimals, info.symbol, info.name),
            logoURI: info.logoURI ?? null
          });
        }
        map.set(info.chainId, chainMap);
      }
    }
    return map;
  }, [listsState]);
  const logoIndex = useMemo(() => {
    const index = /* @__PURE__ */ new Map();
    for (const chainMap of tokensByChain.values()) {
      for (const [key, info] of chainMap) {
        if (info.logoURI && !index.has(key)) {
          index.set(key, info.logoURI);
        }
      }
    }
    return index;
  }, [tokensByChain]);
  const ctx = useMemo(() => ({
    getTokenListTokens(chainId) {
      return Array.from(tokensByChain.get(chainId)?.values() ?? []);
    },
    getTokenListLogoUrl(address) {
      return logoIndex.get(address.toLowerCase()) ?? null;
    },
    getTokenListToken(chainId, address) {
      return tokensByChain.get(chainId)?.get(address.toLowerCase());
    }
  }), [tokensByChain, logoIndex]);
  return <TokenListsContext.Provider value={ctx}>
      {children}
    </TokenListsContext.Provider>;
}
export function useTokenLists() {
  const context = useContext(TokenListsContext);
  if (context === void 0) {
    throw new Error("useTokenLists must be used within a TokenListsContextProvider");
  }
  return context;
}
export function useTokenListLogoUrl(address) {
  const { getTokenListLogoUrl } = useTokenLists();
  return address ? getTokenListLogoUrl(address) : null;
}
